import React, { useState, useEffect, Suspense, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTh, FaMapMarkedAlt, FaChartBar } from 'react-icons/fa';
import { useTeamManagement } from 'hooks/useTeamManagement';
import Loader from 'components/loaders/Loader';
import Modal from 'components/modal/Modal';
import { fireSwal } from 'utils/swalConfig';

// Lazily import heavy components
const MApprovalQueue = React.lazy(() => import('components/card/MApprovalQueue'));
const MTeamGrid = React.lazy(() => import('components/card/MTeamGrid'));
const JourneyMap = React.lazy(() => import('components/card/JourneyMap'));
const POAPAnalytics = React.lazy(() => import('components/card/POAPAnalytics'));
const MTeamDetailPanel = React.lazy(() => import('components/card/MTeamDetailPanel'));

// --- Animated View Switcher Sub-Component ---
const ViewSwitcher = ({ activeView, setActiveView }) => {
    const views = [
        { id: 'dashboard', label: 'Dashboard', icon: <FaTh /> },
        { id: 'journey', label: 'Journey Map', icon: <FaMapMarkedAlt /> },
        { id: 'analytics', label: 'POAP Analytics', icon: <FaChartBar /> },
    ];

    return (
        <div className="flex space-x-2 p-1 bg-gray-200/70 dark:bg-navy-800 rounded-xl">
            {views.map((view) => (
                <button
                    key={view.id}
                    onClick={() => setActiveView(view.id)}
                    className={`relative w-full flex items-center justify-center gap-1 px-3 py-2 font-semibold rounded-lg transition-colors duration-300 ${activeView === view.id ? 'text-brand-500' : 'text-gray-600 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-navy-900/50'}`}
                >
                    {activeView === view.id && (
                        <motion.div
                            layoutId="activeViewIndicator"
                            className="absolute inset-0 bg-white dark:bg-navy-900 rounded-lg shadow"
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                    )}
                    <span className="relative z-10">{view.icon}</span>
                    <span className="relative z-10">{view.label}</span>
                </button>
            ))}
        </div>
    );
};

// --- Main Component ---
const ManageTeam = () => {
    const [activeView, setActiveView] = useState('dashboard');
    const [modalState, setModalState] = useState({ isOpen: false, request: null });
    const [detailPanelTeamId, setDetailPanelTeamId] = useState(null);
    const [highlightId, setHighlightId] = useState(null);
    const location = useLocation();
    const queueRef = useRef(null);
    
    const {
        isLoading,
        teamsWithStats,
        poaps,
        pendingApprovals,
        actions,
    } = useTeamManagement();

    const detailPanelTeam = useMemo(
        () => teamsWithStats.find(t => t.id === detailPanelTeamId),
        [teamsWithStats, detailPanelTeamId]
    );

    // Effect to handle highlighting, scrolling, and modal opening
    useEffect(() => {
        const { highlightId: idToHighlight, scrollToQueue, approvalRequestId } = location.state || {};
        
        // Handle highlight and scroll
        if (idToHighlight) {
            setHighlightId(idToHighlight);
            if (scrollToQueue && queueRef.current) {
                queueRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            const timer = setTimeout(() => setHighlightId(null), 1600);
            window.history.replaceState({}, document.title);
            return () => clearTimeout(timer);
        }

        // Handle direct modal opening (original functionality)
        if (approvalRequestId && pendingApprovals.length > 0) {
            const requestToApprove = pendingApprovals.find(req => req.id === approvalRequestId);
            if (requestToApprove) {
                setModalState({ isOpen: true, request: requestToApprove });
                window.history.replaceState({}, document.title);
            }
        }
    }, [location.state, pendingApprovals]);

    const handleConfirmApproval = async () => {
        if (modalState.request) {
            await actions.approveRequest(modalState.request);
        }
        setModalState({ isOpen: false, request: null });
    };

    const handleDenyClick = async (request) => {
        const result = await fireSwal({
            title: 'Are you sure?',
            html: `This will deny the request from <strong>${request.team.name}</strong> for completing "<strong>${request.step.poapTitle}</strong>".`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Deny Request',
            actionType: 'delete',
        });
        if (result.isConfirmed) {
            actions.denyRequest(request.id);
        }
    };

    if (isLoading) return <Loader />;

    return (
        <div className="mt-5 space-y-8 pt-[60px] lg:pt-[20px]">
            <header className="p-4 bg-white dark:bg-navy-800 rounded-2xl shadow-sm">
                <h1 className="text-4xl font-bold text-navy-700 dark:text-white">Team Management Center</h1>
                <p className="text-md text-gray-500 dark:text-gray-400 mt-1">Oversee approvals, track progress, and manage your teams.</p>
            </header>

            <ViewSwitcher activeView={activeView} setActiveView={setActiveView} />

            <main>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeView}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Suspense fallback={<Loader />}>
                            {activeView === 'dashboard' && (
                                <div className="space-y-8">
                                    <div ref={queueRef}>
                                        <MApprovalQueue
                                            pendingApprovals={pendingApprovals}
                                            highlightId={highlightId}
                                            onApprove={(req) => setModalState({ isOpen: true, request: req })}
                                            onDeny={handleDenyClick}
                                        />
                                    </div>
                                    <MTeamGrid teams={teamsWithStats} onSelectTeam={setDetailPanelTeamId} />
                                </div>
                            )}
                            {activeView === 'journey' && <JourneyMap poaps={poaps} teamsWithStats={teamsWithStats} onSelectTeam={setDetailPanelTeamId} />}
                            {activeView === 'analytics' && <POAPAnalytics poaps={poaps} teams={teamsWithStats} onSelectTeam={setDetailPanelTeamId} />}
                        </Suspense>
                    </motion.div>
                </AnimatePresence>
            </main>

            <AnimatePresence>
                {detailPanelTeam && (
                    <Suspense fallback={<div className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center"><Loader /></div>}>
                          <MTeamDetailPanel team={detailPanelTeam} poaps={poaps} onClose={() => setDetailPanelTeamId(null)} />
                    </Suspense>
                )}
            </AnimatePresence>

            {modalState.isOpen && modalState.request && (
                <Modal onClose={() => setModalState({ isOpen: false, request: null })}>
                    <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-2">Confirm Approval</h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">
                        You are about to award <span className="font-bold text-brand-500">{modalState.request.step.points} points</span> to <span className="font-bold text-navy-700 dark:text-white">{modalState.request.team.name}</span> for completing the step: <span className="font-bold text-navy-700 dark:text-white">"{modalState.request.step.poapTitle}"</span>.
                    </p>
                    <div className="flex justify-end space-x-3">
                        <button className="px-4 py-2 bg-gray-200 dark:bg-navy-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-navy-600 focus:outline-none" onClick={() => setModalState({ isOpen: false, request: null })}>Cancel</button>
                        <button className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 focus:outline-none" onClick={handleConfirmApproval}>Confirm & Award Points</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ManageTeam;